import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Package, Plus, X, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { hasRole, schoolIdFromUser } from '../lib/rbac';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray50: '#F9FAFB', gray100: '#F3F4F6',
  gray200: '#E5E7EB', gray500: '#6B7280', gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error: '#991B1B', errorBg: '#FEE2E2',
  warning: '#92400E', warningBg: '#FEF3C7',
  info: '#1E40AF', infoBg: '#DBEAFE',
  orange: '#EA580C', orangeBg: '#FFF7ED',
};

const BN = {
  title: 'Inventori & Aset Sekolah', subtitle: 'aset', back: '← Laman Utama',
  addAsset: '+ Tambah Aset', tabs: ['Senarai', 'Pelupusan', 'Statistik'] as const,
  search: 'Cari', searchPh: 'Tag, nama, no. siri…',
  allCat: 'Semua Kategori', allCond: 'Semua Keadaan', allStatus: 'Semua Status',
  prev: 'Sebelum', next: 'Seterusnya', showing: 'Menunjukkan', to: 'hingga', of: 'daripada',
  emptyTitle: 'Tiada Aset', emptyBody: 'Tiada rekod aset dijumpai.',
  addTitle: 'Daftar Aset Baharu', assetTag: 'Tag Aset *', nameEn: 'Nama (EN) *',
  nameMs: 'Nama (MS)', category: 'Kategori *', condition: 'Keadaan *',
  status: 'Status *', location: 'Lokasi', supplier: 'Pembekal',
  purchaseDate: 'Tarikh Beli', purchasePrice: 'Harga (BND)',
  serialNumber: 'No. Siri', notes: 'Nota',
  save: 'Simpan', saving: 'Menyimpan…', cancel: 'Batal',
  disposeTitle: 'Rekod Pelupusan', disposalDate: 'Tarikh Lupus *', reason: 'Sebab *',
  authorisedBy: 'Diluluskan Oleh', dispose: 'Lupuskan',
  totalAssets: 'Jumlah Aset', inUse: 'Sedang Digunakan', condemned: 'Dikondemnasi',
  retry: 'Cuba Lagi', errLoad: 'Gagal memuatkan.',
  updateCond: 'Kemaskini Keadaan',
};

const EN = {
  title: 'School Inventory & Assets', subtitle: 'assets', back: '← Home',
  addAsset: '+ Add Asset', tabs: ['Assets', 'Disposals', 'Statistics'] as const,
  search: 'Search', searchPh: 'Tag, name, serial…',
  allCat: 'All Categories', allCond: 'All Conditions', allStatus: 'All Status',
  prev: 'Previous', next: 'Next', showing: 'Showing', to: 'to', of: 'of',
  emptyTitle: 'No Assets', emptyBody: 'No asset records found.',
  addTitle: 'Register New Asset', assetTag: 'Asset Tag *', nameEn: 'Name (EN) *',
  nameMs: 'Name (MS)', category: 'Category *', condition: 'Condition *',
  status: 'Status *', location: 'Location', supplier: 'Supplier',
  purchaseDate: 'Purchase Date', purchasePrice: 'Price (BND)',
  serialNumber: 'Serial Number', notes: 'Notes',
  save: 'Save', saving: 'Saving…', cancel: 'Cancel',
  disposeTitle: 'Record Disposal', disposalDate: 'Disposal Date *', reason: 'Reason *',
  authorisedBy: 'Authorised By', dispose: 'Dispose',
  totalAssets: 'Total Assets', inUse: 'In Use', condemned: 'Condemned',
  retry: 'Retry', errLoad: 'Failed to load.',
  updateCond: 'Update Condition',
};

const CATEGORIES = ['FURNITURE','ICT_EQUIPMENT','SPORTS','LABORATORY','AUDIO_VISUAL','MUSICAL','KITCHEN','MAINTENANCE','CLASSROOM','OTHER'];
const CAT_LABELS: Record<string, { ms: string; en: string; color: string }> = {
  FURNITURE:      { ms: 'Perabot',       en: 'Furniture',      color: '#92400E' },
  ICT_EQUIPMENT:  { ms: 'Peralatan ICT', en: 'ICT Equipment',  color: '#1E40AF' },
  SPORTS:         { ms: 'Sukan',         en: 'Sports',         color: '#166534' },
  LABORATORY:     { ms: 'Makmal',        en: 'Laboratory',     color: '#6D28D9' },
  AUDIO_VISUAL:   { ms: 'Audio-Visual',  en: 'Audio-Visual',   color: '#0F766E' },
  MUSICAL:        { ms: 'Muzik',         en: 'Musical',        color: '#B45309' },
  KITCHEN:        { ms: 'Dapur',         en: 'Kitchen',        color: '#BE185D' },
  MAINTENANCE:    { ms: 'Penyelenggaraan', en: 'Maintenance',  color: '#4B5563' },
  CLASSROOM:      { ms: 'Bilik Darjah',  en: 'Classroom',      color: '#0369A1' },
  OTHER:          { ms: 'Lain-lain',     en: 'Other',          color: '#6B7280' },
};
const COND_CFG: Record<string, { label: { ms: string; en: string }; color: string; bg: string }> = {
  NEW:       { label: { ms: 'Baharu',   en: 'New' },        color: '#166534', bg: '#DCFCE7' },
  GOOD:      { label: { ms: 'Baik',     en: 'Good' },       color: '#1E40AF', bg: '#DBEAFE' },
  FAIR:      { label: { ms: 'Sederhana',en: 'Fair' },       color: '#92400E', bg: '#FEF3C7' },
  POOR:      { label: { ms: 'Lemah',    en: 'Poor' },       color: '#B45309', bg: '#FFF7ED' },
  CONDEMNED: { label: { ms: 'Kondemnasi',en: 'Condemned' }, color: '#991B1B', bg: '#FEE2E2' },
};
const STATUS_CFG: Record<string, { label: { ms: string; en: string }; color: string; bg: string }> = {
  IN_USE:       { label: { ms: 'Sedang Digunakan', en: 'In Use' },        color: '#166534', bg: '#DCFCE7' },
  IN_STORE:     { label: { ms: 'Dalam Stor',       en: 'In Store' },      color: '#1E40AF', bg: '#DBEAFE' },
  UNDER_REPAIR: { label: { ms: 'Sedang Dibaiki',   en: 'Under Repair' },  color: '#92400E', bg: '#FEF3C7' },
  DISPOSED:     { label: { ms: 'Dilupuskan',       en: 'Disposed' },      color: '#4B5563', bg: '#F3F4F6' },
  ON_LOAN:      { label: { ms: 'Dipinjam',         en: 'On Loan' },       color: '#0F766E', bg: '#CCFBF1' },
};

const cardShadow = '0 1px 3px rgba(0,0,0,0.08)';

interface Props { user: AuthUser }
interface Asset { id: string; asset_tag: string; name_en: string; name_ms: string | null; category: string; condition: string; status: string; location: string | null; supplier: string | null; purchase_date: string | null; purchase_price: number | null; serial_number: string | null; notes: string | null; created_at: string }
interface Stats { byCategory: Array<{ category: string; cnt: string }>; byCondition: Array<{ condition: string; cnt: string }>; byStatus: Array<{ status: string; cnt: string }> }
interface PageResult { data: Asset[]; total: number; page: number }

export function InventoryPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  usePageTitle(UI.title);

  const canEdit  = hasRole(user, 'super_admin', 'admin', 'principal', 'clerk');
  const schoolId = schoolIdFromUser(user) ?? '';

  const [tab,     setTab]     = useState<'assets' | 'disposals' | 'stats'>('assets');
  const [assets,  setAssets]  = useState<Asset[]>([]);
  const [stats,   setStats]   = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [search,  setSearch]  = useState('');
  const [catFilter,  setCatFilter]  = useState('');
  const [condFilter, setCondFilter] = useState('');
  const [statFilter, setStatFilter] = useState('');
  const [showAdd,    setShowAdd]    = useState(false);
  const [showDispose, setShowDispose] = useState<string | null>(null);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [form, setForm] = useState({
    assetTag: '', nameEn: '', nameMs: '', category: 'FURNITURE',
    condition: 'GOOD', status: 'IN_USE', location: '', supplier: '',
    purchaseDate: '', purchasePrice: '', serialNumber: '', notes: '',
  });
  const [disposeForm, setDisposeForm] = useState({ disposalDate: '', reason: '', authorisedBy: '' });
  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ schoolId, page: String(page) });
      if (search.trim()) p.set('q', search.trim());
      if (catFilter)  p.set('category', catFilter);
      if (condFilter) p.set('condition', condFilter);
      if (statFilter) p.set('status', statFilter);
      const res = await apiGet<PageResult>(`/api/inventory/assets?${p}`);
      setAssets(res.data); setTotal(res.total);
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, page, search, catFilter, condFilter, statFilter, UI.errLoad]);

  const loadStats = useCallback(async () => {
    setLoading(true); setError(null);
    try { setStats(await apiGet<Stats>(`/api/inventory/stats?schoolId=${schoolId}`)); }
    catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, UI.errLoad]);

  useEffect(() => { if (tab === 'assets') load(); }, [tab, load]);
  useEffect(() => { if (tab === 'stats') loadStats(); }, [tab, loadStats]);

  const submitAsset = async () => {
    if (!form.assetTag.trim() || !form.nameEn.trim()) return;
    setSaving(true);
    try {
      await apiPost(`/api/inventory/assets?schoolId=${schoolId}`, {
        assetTag: form.assetTag, nameEn: form.nameEn, nameMs: form.nameMs || undefined,
        category: form.category, condition: form.condition, status: form.status,
        location: form.location || undefined, supplier: form.supplier || undefined,
        purchaseDate: form.purchaseDate || undefined,
        purchasePrice: form.purchasePrice ? parseFloat(form.purchasePrice) : undefined,
        serialNumber: form.serialNumber || undefined, notes: form.notes || undefined,
      });
      setShowAdd(false);
      setForm({ assetTag: '', nameEn: '', nameMs: '', category: 'FURNITURE', condition: 'GOOD', status: 'IN_USE', location: '', supplier: '', purchaseDate: '', purchasePrice: '', serialNumber: '', notes: '' });
      setToast({ message: lang === 'en' ? 'Asset registered.' : 'Aset didaftarkan.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const submitDisposal = async () => {
    if (!showDispose || !disposeForm.disposalDate || !disposeForm.reason.trim()) return;
    setSaving(true);
    try {
      await apiPost('/api/inventory/disposals', { assetId: showDispose, ...disposeForm });
      setShowDispose(null);
      setDisposeForm({ disposalDate: '', reason: '', authorisedBy: '' });
      setToast({ message: lang === 'en' ? 'Asset disposed.' : 'Aset dilupuskan.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '14px', color: C.gray900, backgroundColor: C.white, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '4px', display: 'block' };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
      <div style={{ marginBottom: '24px' }}>
        <Link to="/" style={{ fontSize: '13px', color: C.gray500, textDecoration: 'none' }}>{UI.back}</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.orangeBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={20} color={C.orange} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: C.gray900 }}>{UI.title}</h1>
              <p style={{ margin: 0, fontSize: '13px', color: C.gray500 }}>{total} {UI.subtitle}</p>
            </div>
          </div>
          {canEdit && tab === 'assets' && (
            <button onClick={() => setShowAdd(true)} style={{ padding: '9px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: C.black }}>
              {UI.addAsset}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
        {(['assets', 'disposals', 'stats'] as const).map((t, i) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 20px', border: 'none', background: 'transparent', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: tab === t ? C.black : C.gray500, borderBottom: tab === t ? `2px solid ${C.yellow}` : '2px solid transparent', marginBottom: '-1px' }}>
            {lang === 'en' ? EN.tabs[i] : BN.tabs[i]}
          </button>
        ))}
      </div>

      {tab === 'assets' && (
        <>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={UI.searchPh}
              style={{ ...inputStyle, width: '220px' }} onKeyDown={e => { if (e.key === 'Enter') { setPage(1); load(); } }} />
            {[
              { val: catFilter,  set: setCatFilter,  opts: CATEGORIES.map(c => ({ v: c, l: lang === 'en' ? CAT_LABELS[c]?.en ?? c : CAT_LABELS[c]?.ms ?? c })), placeholder: UI.allCat },
              { val: condFilter, set: setCondFilter, opts: Object.keys(COND_CFG).map(c => ({ v: c, l: lang === 'en' ? COND_CFG[c]!.label.en : COND_CFG[c]!.label.ms })), placeholder: UI.allCond },
              { val: statFilter, set: setStatFilter, opts: Object.keys(STATUS_CFG).map(s => ({ v: s, l: lang === 'en' ? STATUS_CFG[s]!.label.en : STATUS_CFG[s]!.label.ms })), placeholder: UI.allStatus },
            ].map(({ val, set, opts, placeholder }) => (
              <select key={placeholder} value={val} onChange={e => { set(e.target.value); setPage(1); }} style={{ ...inputStyle, width: '170px' }}>
                <option value="">{placeholder}</option>
                {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            ))}
            <button onClick={() => { setPage(1); load(); }} style={{ padding: '8px 16px', borderRadius: '6px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>{UI.search}</button>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>…</div>}
          {error && <div style={{ padding: '16px', background: C.errorBg, color: C.error, borderRadius: '8px', marginBottom: '16px' }}>{error} <button onClick={load} style={{ marginLeft: '8px', color: C.error, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>{UI.retry}</button></div>}

          {!loading && !error && assets.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
              <Package size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p style={{ fontWeight: 600, margin: 0 }}>{UI.emptyTitle}</p>
              <p style={{ margin: '4px 0 0', fontSize: '13px' }}>{UI.emptyBody}</p>
            </div>
          )}

          <div style={{ display: 'grid', gap: '10px' }}>
            {assets.map(a => {
              const cond  = COND_CFG[a.condition];
              const stat  = STATUS_CFG[a.status];
              const catCfg = CAT_LABELS[a.category];
              return (
                <div key={a.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '16px', boxShadow: cardShadow, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 700, fontSize: '14px', fontFamily: 'monospace', background: C.gray100, padding: '2px 8px', borderRadius: '4px' }}>{a.asset_tag}</span>
                      <span style={{ fontWeight: 600, fontSize: '14px', color: C.gray900 }}>{lang === 'en' ? a.name_en : (a.name_ms ?? a.name_en)}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: C.gray500 }}>
                      {lang === 'en' ? catCfg?.en : catCfg?.ms}
                      {a.location ? ` · ${a.location}` : ''}
                      {a.serial_number ? ` · S/N: ${a.serial_number}` : ''}
                      {a.purchase_price ? ` · BND ${a.purchase_price.toFixed(2)}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: cond?.bg, color: cond?.color }}>{lang === 'en' ? cond?.label.en : cond?.label.ms}</span>
                    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: stat?.bg, color: stat?.color }}>{lang === 'en' ? stat?.label.en : stat?.label.ms}</span>
                    {canEdit && a.status !== 'DISPOSED' && (
                      <button onClick={() => setShowDispose(a.id)} style={{ padding: '5px 10px', borderRadius: '6px', background: C.errorBg, border: `1px solid ${C.error}20`, fontSize: '11px', cursor: 'pointer', color: C.error, fontWeight: 600 }}>
                        {lang === 'en' ? 'Dispose' : 'Lupus'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {total > LIMIT && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', fontSize: '13px', color: C.gray500 }}>
              <span>{UI.showing} {(page - 1) * LIMIT + 1} {UI.to} {Math.min(page * LIMIT, total)} {UI.of} {total}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}><ChevronLeft size={14} /></button>
                <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page * LIMIT >= total ? 'default' : 'pointer', opacity: page * LIMIT >= total ? 0.5 : 1 }}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'disposals' && (
        <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
          <BarChart3 size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
          <p>{lang === 'en' ? 'Disposal records are logged from the Assets tab.' : 'Rekod pelupusan dilog dari tab Aset.'}</p>
        </div>
      )}

      {tab === 'stats' && stats && (
        <div style={{ display: 'grid', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {[
              { label: UI.totalAssets, value: stats.byStatus.reduce((s, c) => s + parseInt(c.cnt, 10), 0), color: C.orange, bg: C.orangeBg },
              { label: UI.inUse, value: parseInt(stats.byStatus.find(s => s.status === 'IN_USE')?.cnt ?? '0', 10), color: C.success, bg: C.successBg },
              { label: UI.condemned, value: parseInt(stats.byCondition.find(s => s.condition === 'CONDEMNED')?.cnt ?? '0', 10), color: C.error, bg: C.errorBg },
            ].map(card => (
              <div key={card.label} style={{ background: card.bg, borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 800, color: card.color }}>{card.value}</div>
                <div style={{ fontSize: '13px', color: card.color, marginTop: '4px' }}>{card.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {[
              { title: lang === 'en' ? 'By Category' : 'Mengikut Kategori', data: stats.byCategory.map(c => ({ label: lang === 'en' ? (CAT_LABELS[c.category]?.en ?? c.category) : (CAT_LABELS[c.category]?.ms ?? c.category), value: parseInt(c.cnt, 10), color: CAT_LABELS[c.category]?.color ?? '#9ca3af' })) },
              { title: lang === 'en' ? 'By Condition' : 'Mengikut Keadaan', data: stats.byCondition.map(c => ({ label: lang === 'en' ? (COND_CFG[c.condition]?.label.en ?? c.condition) : (COND_CFG[c.condition]?.label.ms ?? c.condition), value: parseInt(c.cnt, 10), color: COND_CFG[c.condition]?.color ?? '#9ca3af' })) },
            ].map(({ title, data }) => {
              const total = data.reduce((s, d) => s + d.value, 0);
              return (
                <div key={title} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px' }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: 700 }}>{title}</h3>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {data.map(d => (
                      <div key={d.label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                          <span style={{ color: C.gray700 }}>{d.label}</span>
                          <span style={{ fontWeight: 700 }}>{d.value} ({Math.round((d.value / Math.max(1, total)) * 100)}%)</span>
                        </div>
                        <div style={{ height: '6px', background: C.gray100, borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.round((d.value / Math.max(1, total)) * 100)}%`, background: d.color, borderRadius: '3px', transition: 'width 0.3s' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Asset Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '540px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.addTitle}</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { key: 'assetTag', label: UI.assetTag, type: 'text', full: true },
                { key: 'nameEn', label: UI.nameEn, type: 'text', full: false },
                { key: 'nameMs', label: UI.nameMs, type: 'text', full: false },
                { key: 'location', label: UI.location, type: 'text', full: false },
                { key: 'supplier', label: UI.supplier, type: 'text', full: false },
                { key: 'purchaseDate', label: UI.purchaseDate, type: 'date', full: false },
                { key: 'purchasePrice', label: UI.purchasePrice, type: 'number', full: false },
                { key: 'serialNumber', label: UI.serialNumber, type: 'text', full: false },
              ].map(f => (
                <div key={f.key} style={{ gridColumn: f.full ? '1 / -1' : 'span 1' }}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={form[f.key as keyof typeof form]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={inputStyle} />
                </div>
              ))}
              {[
                { key: 'category', label: UI.category, opts: CATEGORIES.map(c => ({ v: c, l: lang === 'en' ? CAT_LABELS[c]?.en ?? c : CAT_LABELS[c]?.ms ?? c })) },
                { key: 'condition', label: UI.condition, opts: Object.keys(COND_CFG).map(c => ({ v: c, l: lang === 'en' ? COND_CFG[c]!.label.en : COND_CFG[c]!.label.ms })) },
                { key: 'status', label: UI.status, opts: Object.keys(STATUS_CFG).map(s => ({ v: s, l: lang === 'en' ? STATUS_CFG[s]!.label.en : STATUS_CFG[s]!.label.ms })) },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <select value={form[f.key as keyof typeof form]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={inputStyle}>
                    {f.opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>{UI.notes}</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  style={{ ...inputStyle, height: '60px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submitAsset} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.save}
              </button>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Dispose Modal */}
      {showDispose && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '420px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.disposeTitle}</h2>
              <button onClick={() => setShowDispose(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[{ key: 'disposalDate', label: UI.disposalDate, type: 'date' }, { key: 'authorisedBy', label: UI.authorisedBy, type: 'text' }].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={disposeForm[f.key as keyof typeof disposeForm]}
                    onChange={e => setDisposeForm(p => ({ ...p, [f.key]: e.target.value }))} style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>{UI.reason}</label>
                <textarea value={disposeForm.reason} onChange={e => setDisposeForm(p => ({ ...p, reason: e.target.value }))}
                  style={{ ...inputStyle, height: '80px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submitDisposal} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.errorBg, border: `1px solid ${C.error}40`, color: C.error, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.dispose}
              </button>
              <button onClick={() => setShowDispose(null)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
